import { Color } from '@lidojs/design-utils';
import { Input, Textarea } from "@heroui/react";
import React, { FC } from 'react';
import { useEditor } from '../../../hooks';
import { ConfigCreative } from '../../../types';
import ColorPickerPopover from '../ColorPickerPopover';

interface ConfigInputProps {
  config: ConfigCreative;
  onChange: (id: string, value: string, type?: string) => void;
}

const TextareaInput: FC<ConfigInputProps> = ({ config, onChange }) => (
  <Textarea
    css={{
      fontFamily: 'monospace',
      fontSize: '12px',
    }}
    maxRows={10}
    minRows={4}
    name={config.id}
    placeholder="Enter content here..."
    value={config.value.toString()}
    onChange={(e) => onChange(config.id, e.target.value, 'textarea')}
  />
);

const ColorInput: FC<ConfigInputProps> = ({ config, onChange }) => (
  <div css={{ display: 'flex', alignItems: 'center', gap: 8 }}>
    <ColorPickerPopover
      color={config.value.toString()}
      event="click"
      onChange={(color) => onChange(config.id, new Color(color).toRgbString(), 'color')}
    >
      <div
        className="w-10 h-10 border rounded-lg cursor-pointer"
        style={{ backgroundColor: config.value.toString() }}
      />
    </ColorPickerPopover>
    <span css={{ fontSize: 12, color: '#666' }}>{config.value.toString()}</span>
  </div>
);

const NumberInput: FC<ConfigInputProps> = ({ config, onChange }) => (
  <Input
    max={9999}
    min={1}
    name={config.id}
    type="number"
    value={config.value.toString()}
    onChange={(e) => onChange(config.id, e.target.value)}
  />
);

const CONFIG_INPUT_COMPONENTS: Record<string, FC<ConfigInputProps>> = {
  textarea: TextareaInput,
  color: ColorInput,
  number: NumberInput,
};

const ConfigInput: FC<ConfigInputProps> = ({ config, onChange }) => {
  const InputComponent = CONFIG_INPUT_COMPONENTS[config.type || 'number'];
  return <InputComponent config={config} onChange={onChange} />;
};

export const ConfigPremiumettings = () => {
  const { actions, activePage, pages } = useEditor((state) => ({
    activePage: state.activePage,
    pages: state.pages,
  }));

  const { ROOT } = pages[activePage].layers;

  const configSettings: ConfigCreative[] = ROOT.data?.props?.configCreative
    ? Object.values(ROOT.data.props.configCreative)
    : [];

  const handleConfigChange = (id: string, value: string, type?: string) => {
    const isStringType = type === 'textarea' || type === 'color';

    actions.setProp(activePage, 'ROOT', {
      configCreative: {
        ...ROOT.data.props?.configCreative,
        [id]: {
          ...ROOT.data.props?.configCreative[id],
          value: isStringType ? value : parseInt(value, 10),
        },
      },
    });
  };

  if (!configSettings.length) return null;

  return (
    <>
      {configSettings.map((config) => (
        <div key={config.id}>
          <div
            css={{
              display: 'flex',
              justifyContent: 'center',
              flexDirection: 'column',
            }}
          >
            <label css={{ fontWeight: 'bolder' }} htmlFor={config.id}>
              {config.label}
            </label>
            <ConfigInput config={config} onChange={handleConfigChange} />
          </div>
        </div>
      ))}
    </>
  );
};
